import React from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { doesFocusableExist } from '@noriginmedia/norigin-spatial-navigation';

import { rootStore } from '@smart-tv/components/program-sheet/_stores/store-root';
import { uiStore } from '@smart-tv/components/program-sheet/_stores/store-ui';
import { rootStore as stripesRootStore } from '@smart-tv/components/stripes/_stores/store-root';
import { rootStore as npvrRootStore } from '@smart-tv/components/npvr/_stores/store-root';
import { NpvrResourceKey } from '@smart-tv/components/npvr/_stores/store-npvr';
import { RECORDINGS_STRIPE_ID } from '@smart-tv/components/stripes/_utils/types';
import useCollections from '@smart-tv/hooks/use-collections';
import useDialog from '@smart-tv/hooks/use-dialog';
import useQuery from '@smart-tv/hooks/use-query';
import { IconTypes, useToast } from '@smart-tv/hooks/use-toast/use-toast';
import { ContentType } from '@smart-tv/typings/content';
import deleteRecording from '@smart-tv/utils/kmm/deleteRecording';
import startRecording from '@smart-tv/utils/kmm/startRecording';
import decodeProgramSheetParams from '@smart-tv/utils/routing/decode-program-sheet-params';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';

import { closeSheetHandlerRef, recordingDialogContentPropsAtom } from './npvr-atoms';
import { getEpisodeCardFocusKey } from '@smart-tv/components/program-sheet/sheet-helpers';
import { EpisodeExtended } from '@smart-tv/components/program-sheet/types';
import { tvGuideRecordingStatusUpdatesAtom, NPVRStreamAtom } from '@smart-tv/atoms';
import { when } from 'mobx';

const waitForFocusable = (focusKey: string, timeout = 3000): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    const start = performance.now();
    const check = () => {
      if (doesFocusableExist(focusKey)) {
        resolve(true);
      } else if (performance.now() - start >= timeout) {
        resolve(false);
      } else {
        requestAnimationFrame(check);
      }
    };
    check();
  });

const waitForSheetReady = async () =>
  await when(
    () =>
      !rootStore.sheetStore.isPlayerOpen &&
      !rootStore.sheetStore.isRefreshPending &&
      !rootStore.episodesStore.isFetchingEpisodes,
    { timeout: 10000 }
  );

interface QueryParams {
  programsheet: string;
}

export interface UseNPVROptions {
  /** Closes the program sheet. Only the sheet that renders the recording dialog needs to pass it. */
  onClose?: () => void;
}

export const useNPVRSheet = (options?: UseNPVROptions) => {
  const onClose = options?.onClose;

  const { sheet, refreshFocusEpisodes, handleRemovedRecording } = rootStore.sheetStore;
  const { episodes } = rootStore.episodesStore;
  const setTvGuideRecordingStatusUpdates = useSetAtom(tvGuideRecordingStatusUpdatesAtom);
  const [npvrStream, setNPVRStream] = useAtom(NPVRStreamAtom);

  const { programsheet } = useQuery<QueryParams>();
  const { sid, eventId, subProviderId } = decodeProgramSheetParams(programsheet);

  const { openCollections, handleClose: handleCloseCollection } = useCollections();
  const { showToast } = useToast();
  const { handleSetDialogFocusKey } = useDialog();
  const { refresh: refreshNpvrNotifications } = useNpvrNotifications();

  const [isRecording, setIsRecording] = React.useState(sheet?.npvrInfo?.customerRecording ?? false);
  const [isRecordingBeingSet, setIsRecordingBeingSet] = React.useState(false);
  const [isDeletingRecording, setIsDeletingRecording] = React.useState(false);
  const isNPVRProductSheet = subProviderId === 'NPVR';

  const [recordingDialogContentProps, setRecordingDialogContentProps] = useAtom(
    recordingDialogContentPropsAtom
  );

  React.useEffect(() => {
    if (onClose) closeSheetHandlerRef.current = onClose;
  }, [onClose]);

  React.useEffect(() => {
    sheet?.npvrInfo && setIsRecording(sheet?.npvrInfo?.customerRecording);
  }, [sheet?.npvrInfo]);

  React.useEffect(() => {
    if (npvrStream?.progress && npvrStream.length) {
      const isMovie = mapContentTypeToEnum(sheet.contentType) === ContentType.Movies;
      const rawSeconds = isMovie ? 240 : 120;
      const boundaryInSeconds = Math.min(npvrStream.length * 0.05, rawSeconds);
      if (npvrStream.length - npvrStream.progress < boundaryInSeconds) {
        const watchedEpisode = episodes.find(
          (episode: EpisodeExtended) => episode.episodeId === npvrStream.episodeId
        );
        openSeenRecordingDialog(watchedEpisode, npvrStream.focusKey);
      }
      setNPVRStream(undefined);
    }
  }, [npvrStream, setNPVRStream, setRecordingDialogContentProps, episodes, sheet.contentType]);

  const mapContentTypeToEnum = (
    contentType: string
  ): ContentType.Movies | ContentType.Series | undefined => {
    switch (contentType) {
      case 'MOVIES':
        return ContentType.Movies;
      case 'SERIES':
        return ContentType.Series;
      default:
        return undefined;
    }
  };

  const resetNPVRSheet = () => {
    setRecordingDialogContentProps(null);
    closeSheetHandlerRef.current = undefined;
    setNPVRStream(undefined);
  };

  const closeDialogForRecording = React.useCallback(() => {
    setRecordingDialogContentProps(null);
    uiStore.toggleRecordingDialog(false);
  }, [setRecordingDialogContentProps]);

  const updateTvGuideRecordingStatus = React.useCallback(
    (isRecording: boolean, updateSeries = false) => {
      setTvGuideRecordingStatusUpdates((current) => {
        if (updateSeries) {
          const programUpdates = Object.keys(current.programs).reduce((updates, programKey) => {
            const update = current.programs[programKey];
            if (update.sid !== sid || update.seriesId !== sheet.contentId) {
              updates[programKey] = update;
            }
            return updates;
          }, {} as typeof current.programs);

          return {
            programs: {
              ...programUpdates,
              [`${sid}:${eventId}`]: {
                isRecording,
                sid,
                seriesId: sheet.contentId
              }
            },
            series: {
              ...current.series,
              [`${sid}:${sheet.contentId}`]: {
                isRecording,
                startTime: sheet.infoBlock?.startTime ?? sheet.programInfo?.startTime
              }
            }
          };
        }

        return {
          ...current,
          programs: {
            ...current.programs,
            [`${sid}:${eventId}`]: {
              isRecording,
              sid,
              seriesId: sheet.contentType === 'SERIES' ? sheet.contentId : undefined
            }
          }
        };
      });
    },
    [
      setTvGuideRecordingStatusUpdates,
      sid,
      eventId,
      sheet.contentId,
      sheet.contentType,
      sheet.infoBlock?.startTime,
      sheet.programInfo?.startTime
    ]
  );

  const updateDeletedRecordingStatuses = React.useCallback(
    (removedEpisode?: EpisodeExtended) => {
      const deletedEvents: Array<{ sid: number; eventId: number }> = [];
      const providers = removedEpisode?.providers ?? sheet.providers;

      providers.forEach((provider) => {
        provider.versions.forEach((version) => {
          if (version.npvrInfo?.customerRecording && version.sid && version.eventId) {
            deletedEvents.push({ sid: version.sid, eventId: version.eventId });
          }
        });
      });

      setTvGuideRecordingStatusUpdates((current) => {
        const programs = { ...current.programs };

        deletedEvents.forEach((deletedEvent) => {
          programs[`${deletedEvent.sid}:${deletedEvent.eventId}`] = {
            isRecording: false,
            sid: deletedEvent.sid,
            seriesId: sheet.contentType === 'SERIES' ? sheet.contentId : undefined
          };
        });

        return { ...current, programs };
      });
    },
    [setTvGuideRecordingStatusUpdates, sheet]
  );

  // We are guaranteed a sid and eventId if the recording button is shown because the backend determines when it is shown
  // and recordings are only possible for live or future-live content meaning the content is a specific instance for a specific channel
  //  i.e the product sheet has sid + eventId.
  const toggleRecording = React.useCallback(
    async (series = false) => {
      setIsRecordingBeingSet(true);

      try {
        if (series) {
          if (isRecording) {
            if (
              await deleteRecording(undefined, undefined, sheet.contentId, undefined, true, true)
            ) {
              setIsRecording(false);
              updateTvGuideRecordingStatus(false, true);
              npvrRootStore.npvrStore.invalidate([NpvrResourceKey.Scheduled, NpvrResourceKey.Status]);
              showToast('Serieoptagelsen blev annulleret', IconTypes.success);
            }
          } else {
            if (await startRecording(sid, eventId, true)) {
              showToast('Serieoptagelse planlagt', IconTypes.success);
              updateTvGuideRecordingStatus(true, true);
              setIsRecording(true);
              npvrRootStore.npvrStore.invalidate([NpvrResourceKey.Scheduled, NpvrResourceKey.Status]);
            }
          }
        } else {
          if (isRecording) {
            if (await deleteRecording(sid, eventId)) {
              setIsRecording(false);
              updateTvGuideRecordingStatus(false);
              // A single cancel here can mean either a scheduled-only or an already-recorded
              // event - removeRecording covers recordings + expiringSoon, scheduled is still
              // invalidated since we don't have a full NpvrEventItem to match it against.
              npvrRootStore.npvrStore.removeRecording(eventId);
              npvrRootStore.npvrStore.invalidate([NpvrResourceKey.Scheduled, NpvrResourceKey.Status]);
              showToast('Hele optagelsen blev annulleret', IconTypes.success);
            }
          } else {
            if (await startRecording(sid, eventId)) {
              showToast('Optagelse planlagt', IconTypes.success);
              updateTvGuideRecordingStatus(true);
              setIsRecording(true);
              npvrRootStore.npvrStore.invalidate([NpvrResourceKey.Scheduled, NpvrResourceKey.Status]);
            }
          }
        }
      } catch (error) {
        //dont think we have a design for this
        showToast('Der opstod en fejl under ændringen af optagelsen', IconTypes.failure);
        console.error(error);
      } finally {
        await refreshFocusEpisodes();
        setIsRecordingBeingSet(false);
        refreshNpvrNotifications();
      }
    },
    [
      isRecording,
      sheet,
      sid,
      eventId,
      showToast,
      refreshFocusEpisodes,
      setIsRecording,
      setIsRecordingBeingSet,
      updateTvGuideRecordingStatus,
      refreshNpvrNotifications
    ]
  );

  const handleRecordingDialogSelect = React.useCallback(
    (series: boolean) => {
      closeDialogForRecording();

      toggleRecording(series);
    },
    [closeDialogForRecording, toggleRecording]
  );

  const openDialogForRecording = React.useCallback(() => {
    uiStore.toggleRecordingDialog(true);

    const episodeDescription =
      sheet.contentMode == 'NEWS' || sheet.contentMode == 'SPORT'
        ? ''
        : ': S' + sheet?.seasonNumber + ' · E' + sheet?.episodeNumber;
    setRecordingDialogContentProps({
      titleText: isRecording ? 'Hvad vil du annullere?' : 'Hvad vil du optage?',
      primaryButtonText: isRecording
        ? 'Annuller optagelse af episode'
        : `Denne episode${episodeDescription}`,
      secondaryButtonText: isRecording ? 'Annuller serieoptagelse' : 'Denne og fremtidige episoder',
      primaryButtonFunction: () => handleRecordingDialogSelect(false),
      secondaryButtonFunction: () => handleRecordingDialogSelect(true)
    });
  }, [sheet, isRecording, handleRecordingDialogSelect, setRecordingDialogContentProps]);

  const handleToggleRecording = React.useCallback(() => {
    if (
      (sheet.npvrInfo?.customerRecording && !sheet.npvrInfo?.isSeriesRecording) ||
      mapContentTypeToEnum(sheet.contentType) === ContentType.Movies
    ) {
      toggleRecording(false);
    } else {
      openDialogForRecording();
    }
  }, [sheet, toggleRecording, openDialogForRecording]);

  const handleClosingEmptySheet = React.useCallback(() => {
    closeSheetHandlerRef.current && closeSheetHandlerRef.current();

    if (
      stripesRootStore.stripesStore.uiElements.filter(
        (uiElement) => uiElement.id === RECORDINGS_STRIPE_ID
      )[0].content?.contentTotalCount === 1
    ) {
      openCollections && handleCloseCollection();

      const indexOfRecordingsStripe = stripesRootStore.stripesStore.uiElements.findIndex(
        (uiElement) => uiElement.id === RECORDINGS_STRIPE_ID
      );
      stripesRootStore.stripesStore.refresh('0-' + (indexOfRecordingsStripe - 1));
    } else {
      stripesRootStore.stripesStore.refetchUpdateOnFavoriteStripes();
      stripesRootStore.stripesStore.addStripeToRefetch(RECORDINGS_STRIPE_ID);
    }
  }, [openCollections, handleCloseCollection]);

  const handleDeleteRecording = React.useCallback(
    async (removedEpisode?: EpisodeExtended) => {
      try {
        setIsDeletingRecording(true);
        const isMovie = mapContentTypeToEnum(sheet.contentType) === ContentType.Movies;

        const result = await deleteRecording(
          undefined,
          undefined,
          undefined,
          isMovie ? sheet.contentId : removedEpisode?.episodeId ?? sheet.episodeId
        );

        if (result) {
          updateDeletedRecordingStatuses(removedEpisode);
          // Deleted via contentId here (no NpvrEventItem to match against for an optimistic
          // removeRecording), so invalidate recordings + expiringSoon instead.
          npvrRootStore.npvrStore.invalidate([
            NpvrResourceKey.Recordings,
            NpvrResourceKey.ExpiringSoon,
            NpvrResourceKey.Status
          ]);
          const toastMessage = isNPVRProductSheet
            ? 'Optagelsen blev slettet'
            : 'Optagelsen blev slettet.\nProgrammet kan muligvis stadig være tilgængelig via 7-dagsarkivet eller DRTV arkivet.';

          if ((sheet.totalNumberOfEpisodes === 1 || isMovie) && isNPVRProductSheet) {
            handleClosingEmptySheet();
          } else {
            if (!(await handleRemovedRecording())) {
              handleClosingEmptySheet();
            }
            //Finds new episode to focus after removal
            if (removedEpisode && !npvrStream?.fromFocusEpisode) {
              const list = episodes.filter((ep) => !ep.isNextEventforSeries);
              const indexOfRemovedEpisode = list.findIndex(
                (ep) => ep.episodeId === removedEpisode.episodeId
              );
              const index =
                indexOfRemovedEpisode == list.length - 1
                  ? indexOfRemovedEpisode - 1
                  : indexOfRemovedEpisode + 1;
              const contentId = list[index]?.episodeId;
              const focusKey = getEpisodeCardFocusKey(contentId, false);
              if (await waitForFocusable(focusKey)) {
                // update which asset to focus when dialog closes if it exists
                handleSetDialogFocusKey(focusKey);
              }
            }
          }
          showToast(toastMessage, IconTypes.success);
        } else {
          //dont think we have a design for this
          showToast('Der opstod en fejl under sletning af optagelsen', IconTypes.failure);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsDeletingRecording(false);
        closeDialogForRecording();
      }
    },
    [
      closeDialogForRecording,
      sheet,
      showToast,
      handleClosingEmptySheet,
      handleRemovedRecording,
      setIsDeletingRecording,
      handleSetDialogFocusKey,
      episodes,
      isNPVRProductSheet,
      updateDeletedRecordingStatuses
    ]
  );

  const openDeleteRecordingDialog = React.useCallback(
    (episodeForRemoval?: EpisodeExtended) => {
      uiStore.toggleRecordingDialog(true);
      setRecordingDialogContentProps({
        titleText: 'Er du sikker på, at du vil slette optagelsen?',
        //this distinction can be removed when episode title and description headline would match
        description: episodeForRemoval
          ? `S${episodeForRemoval.seasonNumber} · E${episodeForRemoval.episodeNumber} · ${episodeForRemoval.episodeTitle}`
          : sheet.descriptionHeadline,
        primaryButtonText: 'Slet optagelse',
        secondaryButtonText: 'Fortryd',
        primaryButtonFunction: () => handleDeleteRecording(episodeForRemoval ?? undefined),
        secondaryButtonFunction: () => closeDialogForRecording()
      });
    },
    [sheet, handleDeleteRecording, closeDialogForRecording, setRecordingDialogContentProps]
  );

  const openSeenRecordingDialog = React.useCallback(
    (episodeForRemoval?: EpisodeExtended, focusKey?: string) => {
      uiStore.toggleRecordingDialog(true);

      setRecordingDialogContentProps({
        titleText: 'Hvad skal der ske med optagelsen?',
        //this distinction can be removed when episode title and description headline would match
        description: episodeForRemoval
          ? `S${episodeForRemoval.seasonNumber} · E${episodeForRemoval.episodeNumber} · ${episodeForRemoval.episodeTitle}`
          : sheet.descriptionHeadline,
        primaryButtonText: 'Slet',
        secondaryButtonText: 'Behold optagelse',
        primaryButtonFunction: () => {
          handleDeleteRecording(episodeForRemoval ?? undefined);
        },
        secondaryButtonFunction: async () => {
          // After the player closes the sheet reloads, which removes and re-creates the episode list.
          await waitForSheetReady();
          if (focusKey) {
            const focusable = await waitForFocusable(focusKey);
            //if wait times out we let the generel dialog closing handle the focus
            focusable && handleSetDialogFocusKey(focusKey);
          }
          closeDialogForRecording();
        }
      });
    },
    [
      sheet,
      handleDeleteRecording,
      closeDialogForRecording,
      setRecordingDialogContentProps,
      handleSetDialogFocusKey
    ]
  );

  return {
    resetNPVRSheet,
    isRecording,
    isRecordingBeingSet,
    isDeletingRecording,
    recordingDialogContentProps,
    handleToggleRecording,
    openDeleteRecordingDialog,
    closeDialogForRecording,
    mapContentTypeToEnum
  };
};

export default useNPVRSheet;
